import type { ApiClient } from '../apiClient';

/** The firm that set this member up, for white-label co-branding. Null if none. */
export interface MemberFirmBranding {
  name: string;
  logo_url: string | null;
  brand_accent: string | null;
}

export interface VaultDashboard {
  vault: { id: string; name: string; status: string; created_at: string; next_checkin_due_at: string | null; checkin_status: string | null };
  aggregates: { message_count: number; sealed_count: number; recipient_count: number };
  empty_state: boolean;
  /** Present when this member belongs to a firm that has set up branding. */
  firm?: MemberFirmBranding | null;
}
export interface CreateVaultResult {
  vault: { id: string; name: string; status: string; created_at: string };
}
/** How the invitation that confirms a recipient's contact currently stands. */
export type RecipientInviteStatus = 'none' | 'pending' | 'expired' | 'consumed';

export interface RecipientListItem {
  id: string;
  full_name: string;
  relationship: string;
  is_placeholder: boolean;
  placeholder_label: string | null;
  is_minor: boolean;
  has_public_key: boolean;
  /** An email is on file. Without one, nothing can ever be sent to this person. */
  has_email: boolean;
  /**
   * Their email contact is verified — the single fact `delivery-email.worker`
   * requires before it will send. Read from the same `contact_methods` entry the
   * worker gates on, so this badge cannot drift from what actually happens.
   * FALSE means: a message sealed for this person will not arrive.
   */
  contact_verified: boolean;
  /** A guardian email is on file. For a minor, THIS (not contact_verified) is
   *  what makes them deliverable — the worker sends to the guardian. */
  has_guardian_email: boolean;
  invite_status: RecipientInviteStatus;
}
export interface RecipientDetail {
  id: string;
  vault_id: string;
  full_name: string;
  relationship: string;
  is_placeholder: boolean;
  placeholder_label: string | null;
  date_of_birth: string | null;
  email: string | null;
  phone: string | null;
  is_minor: boolean;
  /** A guardian email is stored (the value itself is never returned — PII). */
  has_minor_guardian_email?: boolean;
  /** How a minor's messages are released — see the recipient form. */
  minor_release_mode?: 'guardian_then_child_at_majority' | 'sealed_until_majority';
  notes: string | null;
  // "How we'll find them" reachability channels — decrypted for the owner,
  // exactly like email/phone. At least TWO channels are required for a
  // non-placeholder recipient (counting secondary_contact name+detail as one).
  alt_email: string | null;
  postal_address: string | null;
  secondary_contact_name: string | null;
  secondary_contact_detail: string | null;
}
export type CreateRecipientInput =
  | {
      is_placeholder?: false;
      full_name: string;
      relationship: string;
      date_of_birth: string;
      email?: string;
      phone?: string;
      is_minor?: boolean;
      minor_guardian_email?: string;
      minor_release_mode?: 'guardian_then_child_at_majority' | 'sealed_until_majority';
      notes?: string;
      // At least two of: email, phone, alt_email, postal_address, or a
      // secondary contact (name + detail, together = one channel).
      alt_email?: string;
      postal_address?: string;
      secondary_contact_name?: string;
      secondary_contact_detail?: string;
    }
  | { is_placeholder: true; placeholder_label: string; relationship: string; notes?: string };
export interface UpdateRecipientInput {
  full_name?: string;
  relationship?: string;
  date_of_birth?: string;
  email?: string | null;
  phone?: string | null;
  is_minor?: boolean;
  minor_guardian_email?: string | null;
  minor_release_mode?: 'guardian_then_child_at_majority' | 'sealed_until_majority';
  notes?: string | null;
  // Each: omit = no change; null = clear; string = set. Secondary contact's
  // name + detail must be sent together (both set, or both cleared).
  alt_email?: string | null;
  postal_address?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_detail?: string | null;
}

/** The moment a message finds its recipient — a date, an age, or a life event. */
export interface MessageTriggerSummary {
  type: 'date' | 'age' | 'life_event' | 'inheritance' | 'emergency_only' | null;
  trigger_date: string | null;
  target_age: number | null;
  life_event_type: string | null;
  next_check_at: string | null;
}
export interface MessageListItem {
  id: string;
  title: string | null;
  format: string;
  status: string;
  recipient_id: string | null;
  recipient_full_name: string | null;
  is_emergency_only: boolean;
  sealed_at: string | null;
  delivered_at: string | null;
  created_at: string;
  trigger: MessageTriggerSummary;
  /** Count of live triggers: 0 = no delivery set (sealed orphan), >1 = compound. */
  trigger_count: number;
}

export function getMyVault(api: ApiClient): Promise<VaultDashboard> {
  return api.get<VaultDashboard>('/api/vaults/me');
}
export function listMessages(api: ApiClient, vaultId: string): Promise<{ messages: MessageListItem[] }> {
  return api.get<{ messages: MessageListItem[] }>(`/api/vaults/${vaultId}/messages`);
}
/** Reschedule a not-yet-delivered message to a new date OR a new age. */
export function rescheduleMessage(
  api: ApiClient,
  id: string,
  input: { trigger_date: string } | { target_age: number },
): Promise<unknown> {
  return api.patch(`/api/messages/${id}/trigger`, input);
}
/** The escape hatch — withdraw a message before it is delivered. */
export function withdrawMessage(api: ApiClient, id: string): Promise<unknown> {
  return api.post(`/api/messages/${id}/withdraw`);
}
/** Remove a message entirely — the owner no longer wants it recorded at all. */
export function deleteMessage(api: ApiClient, id: string): Promise<unknown> {
  return api.del(`/api/messages/${id}`);
}
export function renameVault(api: ApiClient, vaultId: string, name: string): Promise<unknown> {
  return api.patch(`/api/vaults/${vaultId}`, { name });
}

/** An emergency release in flight on the owner's vault — that they can still STOP. */
export interface ActiveRelease {
  id: string;
  status: string;
  reason: string;
  initiated_by_name: string | null;
  /** Who opened it: 'guardian' (a reported death, a quorum must confirm),
   * 'executor' (the named executor authorized it alone), or 'head_office' (a
   * head-office break-glass override). Changes the banner copy. */
  authority: 'guardian' | 'executor' | 'head_office';
  challenge_window_ends_at: string | null;
  created_at: string;
}
export function listActiveReleases(api: ApiClient, vaultId: string): Promise<{ releases: ActiveRelease[] }> {
  return api.get<{ releases: ActiveRelease[] }>(`/api/vaults/${vaultId}/emergency-releases`);
}
export function cancelRelease(api: ApiClient, vaultId: string, releaseId: string): Promise<unknown> {
  return api.post(`/api/vaults/${vaultId}/emergency-releases/${releaseId}/cancel`, { reason: 'creator_override' });
}
export function createVault(api: ApiClient): Promise<CreateVaultResult> {
  return api.post<CreateVaultResult>('/api/vaults');
}
export function listRecipients(api: ApiClient, vaultId: string): Promise<{ recipients: RecipientListItem[] }> {
  return api.get<{ recipients: RecipientListItem[] }>(`/api/vaults/${vaultId}/recipients`);
}
export function getRecipient(api: ApiClient, vaultId: string, id: string): Promise<{ recipient: RecipientDetail }> {
  return api.get<{ recipient: RecipientDetail }>(`/api/vaults/${vaultId}/recipients/${id}`);
}
export function createRecipient(api: ApiClient, vaultId: string, input: CreateRecipientInput): Promise<{ recipient: { id: string } }> {
  return api.post<{ recipient: { id: string } }>(`/api/vaults/${vaultId}/recipients`, input);
}
export function updateRecipient(api: ApiClient, vaultId: string, id: string, input: UpdateRecipientInput): Promise<{ recipient: { id: string } }> {
  return api.patch<{ recipient: { id: string } }>(`/api/vaults/${vaultId}/recipients/${id}`, input);
}
export function inviteRecipient(api: ApiClient, vaultId: string, id: string): Promise<{ inviteId: string; inviteUrl: string; expiresAt: string }> {
  return api.post<{ inviteId: string; inviteUrl: string; expiresAt: string }>(`/api/vaults/${vaultId}/recipients/${id}/invite`);
}
